/**
 * Copyright 2026 Perform Content Services Limited. Confidential and Proprietary. All Rights Reserved.
 */

import {ILogger} from '../../logger/LoggerInterface';
import LoggerFactory from '../../logger/LoggerFactory';
import Disposable from '../../lang/Disposable';
import DisposableList from '../../lang/DisposableList';
import IDisposable from '../../lang/IDisposable';
import Subject from '../../rx/Subject';
import {DecodedFrameTimingInformation, RenderedFrameTimingInformation, RtpTimestamp} from './FrameTimingInformation';
import {VideoCodec} from '../decode/bitstream/codec/VideoCodec';
import {MetadataMessagePayload, TransformMessageDataType, TransformMessagePayload} from '../transformation/worker/TransformStreamPipelineWorkerMessage';
import TransformMessageRouter from '../transformation/worker/MessageRouter';

export type FrameTimingInformationManagerContext = {
  codec: VideoCodec;
  streamId: string;
  videoElement: Subject<HTMLVideoElement | null>;
  decodedFrameTimingInformationSubject: Subject<DecodedFrameTimingInformation | null>;
  renderedFrameTimingInformationSubject: Subject<RenderedFrameTimingInformation | null>;
  isEnabledSubject: Subject<boolean>;
  receiver: RTCRtpReceiver;
  mid: string;
  trackId: string;
  transformFunctionNames: string[];
  transformMessageRouter: TransformMessageRouter;
};

export default class FrameTimingInformationManager implements IDisposable {
  private static readonly _transformName = 'frame-timing-information';
  private readonly _logger: ILogger = LoggerFactory.getLogger('FrameTimingInformationManager');
  private readonly _decodedFrameTimingInformationByRtpTimestamp: Map<RtpTimestamp, DecodedFrameTimingInformation> = new Map();
  private readonly _videoElementDisposables: DisposableList = new DisposableList();
  private readonly _disposables: DisposableList = new DisposableList();
  private _currentPlaybackState: 'playing' | 'paused' | 'stalled' | 'ended' | 'error' | 'waiting' = 'waiting';
  private _armedVideoFrameCallbackId: number | null = null;
  private _disposed: boolean = false;

  constructor(context: FrameTimingInformationManagerContext) {
    this.initialize(context);
  }

  public dispose(): void {
    this._disposed = true;
    this._disposables.dispose();
  }

  private initialize(context: FrameTimingInformationManagerContext): void {
    if (!this.canProcessFrameTimingInformation(context.streamId, context.codec, context.receiver, context.mid)) {
      return;
    }

    this.setupVideoElementHandling(context.videoElement, context.renderedFrameTimingInformationSubject);
    this.setupFrameTimingInformationTransform(context.codec, context.mid, context.trackId, context.transformMessageRouter, context.transformFunctionNames, context.decodedFrameTimingInformationSubject);
    context.isEnabledSubject.value = true;
  }

  private canProcessFrameTimingInformation(streamId: string, codec: VideoCodec, receiver: RTCRtpReceiver, mid: string): boolean {
    if (codec !== VideoCodec.H264) {
      this._logger.warn('[%s] Skipping frame timing information for video track because video codec is not H264', streamId);

      return false;
    }

    if (!receiver) {
      this._logger.warn('[%s] Skipping frame timing information for video track because no receiver was found', streamId);

      return false;
    }

    if (!mid) {
      this._logger.warn('[%s] Skipping frame timing information for video track because no mid was found', streamId);

      return false;
    }

    return true;
  }

  private setupFrameTimingInformationTransform(codec: VideoCodec, mid: string, trackId: string, transformMessageRouter: TransformMessageRouter, transformFunctionNames: string[], decodedFrameTimingInformationSubject: Subject<DecodedFrameTimingInformation | null>): void {
    transformMessageRouter.registerRoute<TransformMessagePayload<MetadataMessagePayload>>(TransformMessageDataType.FrameTimingInformation, this.handleFrameTimingMessage.bind(this, VideoCodec[codec], mid!, trackId!, decodedFrameTimingInformationSubject));
    transformFunctionNames.push(FrameTimingInformationManager._transformName);
  }

  private handleFrameTimingMessage(codec: string, mid: string, trackId: string, decodedFrameTimingInformationSubject: Subject<DecodedFrameTimingInformation | null>, message: MetadataMessagePayload): void {
    if (!message) {
      return;
    }

    for (const [rtpTimestamp, metadata] of Object.entries(message)) {
      const rtpTimestampNumber = parseInt(rtpTimestamp, 10);
      const decodedFrameTimingInformation = metadata['decodedFrameTimingInformation'];

      if (decodedFrameTimingInformation) {
        const frameTimingInformation: DecodedFrameTimingInformation = {
          timecode: decodedFrameTimingInformation.timecode ?? null,
          receiveTimestamp: decodedFrameTimingInformation.receiveTimestamp ?? null,
          kind: 'video',
          codec,
          mid,
          trackId,
          sourceTimestamp: null,
          ingestTimestamp: null,
          encodeTimestamp: null,
          transcodeTimestamp: null,
          playbackState: this._currentPlaybackState
        };

        decodedFrameTimingInformationSubject.value = frameTimingInformation;

        if (this._currentPlaybackState === 'playing') {
          this._decodedFrameTimingInformationByRtpTimestamp.set(rtpTimestampNumber, frameTimingInformation);
        }
      }
    }
  }

  private setupVideoElementHandling(videoElementSubject: Subject<HTMLVideoElement | null>, renderedFrameTimingInformationSubject: Subject<RenderedFrameTimingInformation | null>): void {
    this._disposables.add(new Disposable(() => this._decodedFrameTimingInformationByRtpTimestamp.clear()));
    this._disposables.add(this._videoElementDisposables);
    this._disposables.add(videoElementSubject.subscribe(videoElement => {
      this._videoElementDisposables.dispose();

      if (!videoElement) {
        return;
      }

      this.setupVideoEventListeners(videoElement, renderedFrameTimingInformationSubject);
    }));
  }

  private handleVideoFrameCallback(videoElement: HTMLVideoElement, renderedFrameTimingInformationSubject: Subject<RenderedFrameTimingInformation | null>, now: DOMHighResTimeStamp, metadata: VideoFrameCallbackMetadata): void {
    if (this._disposed) {
      return;
    }

    // Re-register the callback to ensure it's called again
    this._armVideoFrameCallback(videoElement, renderedFrameTimingInformationSubject);

    const rtpTimestamp = metadata.rtpTimestamp;

    if (!rtpTimestamp) {
      // TODO (NH): Add a throttled log indicating that no RTP timestamp was found
      // The specification says that it's nullable, but we always have it in a WebRTC context
      return;
    }

    const timestampsToDelete: RtpTimestamp[] = [];

    for (const [timestamp, frameTiming] of this._decodedFrameTimingInformationByRtpTimestamp) {
      if (timestamp > rtpTimestamp) {
        break;
      }

      renderedFrameTimingInformationSubject.value = {
        ...frameTiming,
        renderTimestamp: new Date(performance.timeOrigin + now),
        playbackState: 'playing'
      };

      timestampsToDelete.push(timestamp);
    }

    for (const timestamp of timestampsToDelete) {
      this._decodedFrameTimingInformationByRtpTimestamp.delete(timestamp);
    }
  }

  private setupVideoEventListeners(videoElement: HTMLVideoElement, renderedFrameTimingInformationSubject: Subject<RenderedFrameTimingInformation | null>): void {
    const onPlaying = () => this._currentPlaybackState = 'playing';

    videoElement.addEventListener('playing', onPlaying);
    this._videoElementDisposables.add(new Disposable(() => videoElement.removeEventListener('playing', onPlaying)));

    const onPause = () => this._currentPlaybackState = 'paused';

    videoElement.addEventListener('pause', onPause);
    this._videoElementDisposables.add(new Disposable(() => videoElement.removeEventListener('pause', onPause)));

    const onStalled = () => this._currentPlaybackState = 'stalled';

    videoElement.addEventListener('stalled', onStalled);
    this._videoElementDisposables.add(new Disposable(() => videoElement.removeEventListener('stalled', onStalled)));

    const onWaiting = () => this._currentPlaybackState = 'waiting';

    videoElement.addEventListener('waiting', onWaiting);
    this._videoElementDisposables.add(new Disposable(() => videoElement.removeEventListener('waiting', onWaiting)));

    const onEnded = () => this._currentPlaybackState = 'ended';

    videoElement.addEventListener('ended', onEnded);
    this._videoElementDisposables.add(new Disposable(() => videoElement.removeEventListener('ended', onEnded)));

    const onError = () => this._currentPlaybackState = 'error';

    videoElement.addEventListener('error', onError);
    this._videoElementDisposables.add(new Disposable(() => videoElement.removeEventListener('error', onError)));

    this._armVideoFrameCallback(videoElement, renderedFrameTimingInformationSubject);
    this._videoElementDisposables.add(new Disposable(() => {
      if (this._armedVideoFrameCallbackId) {
        videoElement.cancelVideoFrameCallback(this._armedVideoFrameCallbackId);
      }
    })
    );
  }

  private _armVideoFrameCallback(videoElement: HTMLVideoElement, renderedFrameTimingInformationSubject: Subject<RenderedFrameTimingInformation | null>): void {
    if (!videoElement.requestVideoFrameCallback) {
      return;
    }

    this._armedVideoFrameCallbackId = videoElement.requestVideoFrameCallback(this.handleVideoFrameCallback.bind(this, videoElement, renderedFrameTimingInformationSubject));
  }
}